import { IManagedPaymentMethodDetails, IManagedTokenDelegatePaymentMethodDetails } from '../api/Radom'

const STORAGE_PREFIX = 'radom.paymentSession'
const MAX_SESSION_AGE_MS = 24 * 60 * 60 * 1000

export interface StoredPaymentSession {
  managed?: IManagedPaymentMethodDetails
  tokenDelegate?: IManagedTokenDelegatePaymentMethodDetails
  delegateSubmitted?: boolean
  savedAt?: number
}

const storageKey = (sourceId: string): string => `${STORAGE_PREFIX}.${sourceId}`

const storages = (): Storage[] => {
  if (typeof window === 'undefined') {
    return []
  }

  const available: Storage[] = []
  try {
    available.push(window.localStorage)
  } catch {}
  try {
    available.push(window.sessionStorage)
  } catch {}

  return available
}

const isExpired = (session: StoredPaymentSession): boolean =>
  !session.savedAt || Date.now() - session.savedAt > MAX_SESSION_AGE_MS

export const savePaymentSession = (sourceId: string, session: StoredPaymentSession): void => {
  const payload = JSON.stringify({ ...session, savedAt: session.savedAt ?? Date.now() })

  storages().forEach(storage => {
    try {
      storage.setItem(storageKey(sourceId), payload)
    } catch {}
  })
}

export const loadPaymentSession = (sourceId: string): StoredPaymentSession | undefined => {
  for (const storage of storages()) {
    let session: StoredPaymentSession
    try {
      const raw = storage.getItem(storageKey(sourceId))
      if (!raw) {
        continue
      }

      session = JSON.parse(raw) as StoredPaymentSession
    } catch {
      continue
    }

    if (!session.managed && !session.tokenDelegate) {
      continue
    }

    if (isExpired(session)) {
      clearPaymentSession(sourceId)
      return undefined
    }

    return session
  }

  return undefined
}

export const clearPaymentSession = (sourceId: string): void => {
  storages().forEach(storage => {
    try {
      storage.removeItem(storageKey(sourceId))
    } catch {}
  })
}
